import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import useTokenRevocation from '../../../../hooks/useTokenRevocation';
import { BottomMargin, StartHeadline } from '../../../../styledComponents/styledComponents';
import {
   BlockStatusDot,
   BlockStatusText,
   BlockActionButton,
   BlockPanelContent,
   BlockStatusRow,
   BlockErrorText,
} from '../StyledComponents';

const REVOCATION_GRACE_PERIOD_HOURS = 24;

function TokenRevokePanel({ playbackToken, tokenPayload, isRevoked, onRevokeSuccess }) {
   const { revoke, isLoading: isRevoking, isSuccess, error, reset } = useTokenRevocation({ token: playbackToken });

   useEffect(() => {
      if (isSuccess) {
         onRevokeSuccess();
         reset();
      }
   }, [isSuccess, onRevokeSuccess, reset]);

   const isFetchLoading = isRevoked === null;
   const isRevocable = tokenPayload?.revocable === true;
   const expirationMoment = moment.utc(tokenPayload?.exp);

   const now = moment.utc();
   const isExpired = expirationMoment.isValid() && expirationMoment.isBefore(now);
   const isExpiredOverGracePeriod =
      expirationMoment.isValid() &&
      expirationMoment.isBefore(now.clone().subtract(REVOCATION_GRACE_PERIOD_HOURS, 'hours'));

   return (
      <BottomMargin pxSize={20}>
         <StartHeadline>Playback Token Revocation</StartHeadline>
         <BlockPanelContent>
            {!isRevocable ? (
               <BlockStatusText>Token is not revocable</BlockStatusText>
            ) : (
               <>
                  <BlockStatusRow style={{ gap: '10px' }}>
                     {!isFetchLoading && <BlockStatusDot $blocked={isRevoked} />}
                     <BlockStatusText $blocked={isFetchLoading ? undefined : isRevoked}>
                        {isFetchLoading ? 'Checking revocation status...' : isRevoked ? 'Revoked' : 'Not revoked'}
                     </BlockStatusText>

                     {isExpired && <BlockStatusText>{' | Expired'}</BlockStatusText>}

                     {!isRevoked && !isExpiredOverGracePeriod && (
                        <BlockActionButton
                           $variant='block'
                           disabled={isFetchLoading || isRevoking || !playbackToken}
                           onClick={revoke}
                        >
                           {isRevoking ? 'Revoking...' : 'Revoke'}
                        </BlockActionButton>
                     )}
                  </BlockStatusRow>

                  {error && <BlockErrorText>{error.message}</BlockErrorText>}
               </>
            )}
         </BlockPanelContent>
      </BottomMargin>
   );
}

TokenRevokePanel.propTypes = {
   playbackToken: PropTypes.string.isRequired,
   tokenPayload: PropTypes.object.isRequired,
   isRevoked: PropTypes.bool,
   onRevokeSuccess: PropTypes.func.isRequired,
};

export default TokenRevokePanel;
